import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getInitialGame, isWP, isBP, getPieceType, getPieceAt,
  getLegalMoves, applyMove, finalizeMove, isGameOver, inCheck,
} from "./chess/engine";
import { getBotMove, getBlancheMove, DIFFICULTIES } from "./chess/bot";
import { TROPHIES, loadStats, recordResult } from "./data/trophies";
import { BOARD_THEMES, getPieceStyle, loadPrefs, savePrefs } from "./data/shopItems";
import MainMenu from "./components/MainMenu";
import CrownCoin from "./components/CrownCoin";
import { DS } from "./theme";
import TrophyCase      from "./components/TrophyCase";
import StatsPanel      from "./components/StatsPanel";
import Leaderboard     from "./components/Leaderboard";
import Shop            from "./components/Shop";
import Armoury         from "./components/Armoury";
import AccountSettings from "./components/AccountSettings";
import Blanche         from "./components/Blanche";
import DailyPuzzle     from "./components/DailyPuzzle";
import { narrateMove, getUserPurchases, createTrialSession, saveGameResult } from "./lib/api";
import { COSMETIC_ITEMS } from "./data/cosmeticItems";
import { getPieceStyleFromCosmetic, getBoardColorsFromCosmetic, getSquareEffectClass, getBoardEffectClass } from "./lib/effects";
import { playMove, playCapture, playCheck, playGameOver, playBlancheOpen, configure as configureSound } from "./lib/sound";

const DIFF_DELAYS  = { easy: 350, novice: 500, intermediate: 650, advanced: 950, pro: 1300 };
const BOARD_SQ_MAP = { compact: 58, standard: 68, large: 78 };

// ─────────────────────────────────────────────────────────────────────────────
//  APP
// ─────────────────────────────────────────────────────────────────────────────

export default function GameApp({ multiplayer = null, user = null, onPlayOnline = null, onLogout = null, eloChange = null, opponentDisconnected = false, hasResumeGame = false, resumeExpired = false, onResumeMatch = null }) {
  const [screen, setScreen]   = useState("menu"); // "menu" | "app"
  const [tab, setTab]         = useState("play");
  const [game, setGame]       = useState(() => {
    const saved = localStorage.getItem('checkmate_last_bot_difficulty');
    const g = getInitialGame();
    if (saved) g.difficulty = saved;
    return g;
  });
  const [stats, setStats]     = useState(() => loadStats());
  const [prefs, setPrefs]     = useState(() => loadPrefs());
  const [toasts, setToasts]           = useState([]);
  const [blancheOpen, setBlancheOpen]       = useState(false);
  const [isMobile, setIsMobile]             = useState(window.innerWidth < 900);
  const [blancheNarration, setBlancheNarration] = useState([]);
  const [commentaryOn, setCommentaryOn]     = useState(true);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialLoading, setTrialLoading]     = useState(false);
  const [subTier, setSubTier]               = useState(null); // "arena" | "pro" | null
  const [crowns, setCrowns]                 = useState(0);
  const [elo, setElo]                       = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showOnlineResult, setShowOnlineResult] = useState(false);
  const [hintCount, setHintCount]   = useState(0);
  const [autoHint, setAutoHint]     = useState(null);
  const prevHistoryLen = useRef(0);
  const prevCapWLen    = useRef(0);
  const prevCapBLen    = useRef(0);
  const prevStatus     = useRef("");

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Load crowns + subscription tier on mount
  useEffect(() => {
    if (!user?.token) return;
    getUserPurchases(user.token)
      .then(data => {
        if (data?.crowns !== undefined) setCrowns(data.crowns);
        if (data?.elo    !== undefined) setElo(data.elo);
        if (data?.subscription?.status === 'active') setSubTier(data.subscription.tier || 'arena');
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // Show onboarding once per new user
  useEffect(() => {
    if (user && !localStorage.getItem('checkmate_onboarded')) setShowOnboarding(true);
  }, [user]);

  const { board, turn, status, selected, selectedType, legalMoves, promo,
          history, capW, capB, halfmove, drawOffer, flipped,
          mode, difficulty, botThinking, lastMove } = game;

  const activeTheme = BOARD_THEMES[prefs.theme] || BOARD_THEMES.classic;

  // Crown cosmetics — override theme / piece style when equipped
  const activePieceCosmetic = prefs.activeCosmeticPieces
    ? COSMETIC_ITEMS.find(c => c.id === prefs.activeCosmeticPieces && c.category === 'pieces') : null;
  const activeBoardCosmetic = prefs.activeCosmeticBoard
    ? COSMETIC_ITEMS.find(c => c.id === prefs.activeCosmeticBoard  && c.category === 'board')  : null;
  const cosmeticBoardColors = activeBoardCosmetic ? getBoardColorsFromCosmetic(activeBoardCosmetic) : null;
  const T = cosmeticBoardColors ? { ...activeTheme, ...cosmeticBoardColors } : activeTheme;
  const sqEffectClass   = activePieceCosmetic ? getSquareEffectClass(activePieceCosmetic.effects)   : '';
  const boardEffectClass = activeBoardCosmetic ? getBoardEffectClass(activeBoardCosmetic.effects)    : '';

  const wCheck = useMemo(() => inCheck("white", board), [board]);
  const bCheck = useMemo(() => inCheck("black", board), [board]);
  const over   = isGameOver(status);

  // ── Prefs ──────────────────────────────────────────────────────────────────
  function updatePrefs(patch) {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    savePrefs(next);
  }

  // Apply font scale to document root whenever it changes
  useEffect(() => {
    document.documentElement.style.zoom = prefs.fontScale || 1;
  }, [prefs.fontScale]);

  // ── Game result ────────────────────────────────────────────────────────────
  function handleGameOver(finalStatus) {
    if (!isGameOver(finalStatus)) return;
    let result = "draw";
    let byCheckmate = false;
    if (finalStatus.includes("wins")) {
      const myColor = multiplayer?.playerColor || "white";
      result = finalStatus.startsWith(myColor) ? "win" : "loss";
      byCheckmate = finalStatus.includes("checkmate");
    }
    const moveCount = history.length + (history[history.length - 1]?.black ? 0 : -1);
    const fastWin = result === "win" && moveCount <= 15;
    const { updatedStats, newlyEarned } = recordResult(stats, result, {
      byCheckmate, fastWin, mode,
      difficulty: mode === "bot" ? difficulty : undefined,
    });
    setStats(updatedStats);
    if (newlyEarned.length > 0) {
      const earned = newlyEarned.map(id => TROPHIES.find(t => t.id === id)).filter(Boolean);
      setToasts(prev => [...prev, ...earned]);
    }
    // Save result and award crowns for bot / local PvP (online wins handled server-side)
    if (!multiplayer && user?.token) {
      const pgn = history.map((pair, i) =>
        `${i + 1}. ${pair.white}${pair.black ? ' ' + pair.black : ''}`
      ).join(' ');

      saveGameResult(user.token, {
        winner: result === 'win' ? (multiplayer?.playerColor || 'white') : (result === 'loss' ? 'black' : 'draw'),
        pgn,
        moveCount,
        gameType: mode,
        difficulty: mode === 'bot' ? difficulty : undefined
      })
      .then(data => { if (data?.crowns !== undefined) setCrowns(data.crowns); })
      .catch(() => {});
    }

    // Notify server so the game gets persisted
    if (multiplayer?.socket && multiplayer?.gameId) {
      let winner = "draw";
      if (finalStatus.includes("white wins")) winner = "white";
      else if (finalStatus.includes("black wins")) winner = "black";
      const pgn = history.map((pair, i) =>
        `${i + 1}. ${pair.white}${pair.black ? ' ' + pair.black : ''}`
      ).join(' ');
      multiplayer.socket.emit("game_over", {
        gameId: multiplayer.gameId,
        winner,
        pgn,
        moveCount: history.length + (history[history.length - 1]?.black ? 0 : -1),
      });
    }
  }

  // ── Square click ───────────────────────────────────────────────────────────
  function handleSquare(sq) {
    if (promo || over || botThinking) return;
    if (mode === "bot" && turn === "black") return;
    if (mode === "blanche" && turn === "white") return;
    if (multiplayer && turn !== multiplayer.playerColor) return;
    const ownPiece = (turn === "white" && isWP(sq, board)) || (turn === "black" && isBP(sq, board));
    if (ownPiece) {
      const type  = getPieceType(sq, turn, board);
      const moves = getLegalMoves(sq, type, turn, board);
      setGame(p => ({ ...p, selected: sq, selectedType: type, legalMoves: moves }));
      return;
    }
    if (selected !== null && legalMoves.includes(sq)) {
      const isPromoMove = selectedType === "pawn" &&
        ((turn === "white" && Math.floor(sq / 8) === 0) || (turn === "black" && Math.floor(sq / 8) === 7));
      if (isPromoMove) {
        setGame(p => ({ ...p, promo: { from: selected, to: sq, color: turn }, selected: null, selectedType: null, legalMoves: [] }));
        return;
      }
      // Capture move details before async setGame
      const moveFrom = selected, moveTo = sq, moveType = selectedType;
      setGame(prev => {
        const { board: nb, capturedType } = applyMove(prev.board, prev.selected, prev.selectedType, prev.turn, sq);
        const next = finalizeMove(prev, prev.turn, nb, capturedType, prev.selected, sq, prev.selectedType, null);
        if (isGameOver(next.status)) setTimeout(() => handleGameOver(next.status), 0);
        return next;
      });
      if (multiplayer) {
        multiplayer.socket.emit('make_move', {
          gameId: multiplayer.gameId,
          move: { from: moveFrom, to: moveTo, type: moveType, promo: null },
        });
      }
    } else {
      setGame(p => ({ ...p, selected: null, selectedType: null, legalMoves: [] }));
    }
  }

  // ── Promotion ──────────────────────────────────────────────────────────────
  function handlePromo(piece) {
    if (!promo) return;
    const promoFrom = promo.from, promoTo = promo.to;
    setGame(prev => {
      const { board: nb, capturedType } = applyMove(prev.board, prev.promo.from, "pawn", prev.promo.color, prev.promo.to, piece);
      const next = finalizeMove(prev, prev.promo.color, nb, capturedType, prev.promo.from, prev.promo.to, "pawn", piece);
      if (isGameOver(next.status)) setTimeout(() => handleGameOver(next.status), 0);
      return next;
    });
    if (multiplayer) {
      multiplayer.socket.emit('make_move', {
        gameId: multiplayer.gameId,
        move: { from: promoFrom, to: promoTo, type: "pawn", promo: piece },
      });
    }
  }

  // ── Bot ────────────────────────────────────────────────────────────────────
  const makeBotMove = useCallback(() => {
    setGame(prev => {
      if (prev.mode !== "bot" || prev.turn !== "black" || prev.promo || isGameOver(prev.status))
        return { ...prev, botThinking: false };
      const move = getBotMove(prev.board, prev.difficulty);
      if (!move) return { ...prev, botThinking: false };
      const isPromo = move.type === "pawn" && Math.floor(move.to / 8) === 7;
      const { board: nb, capturedType } = applyMove(prev.board, move.from, move.type, "black", move.to, isPromo ? "queen" : null);
      const next = finalizeMove(prev, "black", nb, capturedType, move.from, move.to, move.type, isPromo ? "queen" : null);
      if (isGameOver(next.status)) setTimeout(() => handleGameOver(next.status), 0);
      return next;
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mode === "bot" && turn === "black" && !promo && !over) {
      setGame(p => ({ ...p, botThinking: true }));
      const t = setTimeout(makeBotMove, DIFF_DELAYS[difficulty] || 600);
      return () => clearTimeout(t);
    }
  }, [mode, turn, promo, status, makeBotMove]); // eslint-disable-line

  // ── BLANCHE play mode ──────────────────────────────────────────────────────
  const makeBlancheMove = useCallback(() => {
    setGame(prev => {
      if (prev.mode !== "blanche" || prev.turn !== "white" || prev.promo || isGameOver(prev.status))
        return { ...prev, botThinking: false };
      const move = getBlancheMove(prev.board, "advanced");
      if (!move) return { ...prev, botThinking: false };
      const isPromo = move.type === "pawn" && Math.floor(move.to / 8) === 0;
      const { board: nb, capturedType } = applyMove(prev.board, move.from, move.type, "white", move.to, isPromo ? "queen" : null);
      const next = finalizeMove(prev, "white", nb, capturedType, move.from, move.to, move.type, isPromo ? "queen" : null);
      if (isGameOver(next.status)) setTimeout(() => handleGameOver(next.status), 0);
      return next;
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (mode === "blanche" && turn === "white" && !promo && !over) {
      setGame(p => ({ ...p, botThinking: true }));
      const t = setTimeout(makeBlancheMove, 900);
      return () => clearTimeout(t);
    }
  }, [mode, turn, promo, status, makeBlancheMove]); // eslint-disable-line

  // ── BLANCHE narration — fires after each move in blanche game ─────────────
  useEffect(() => {
    if (mode !== "blanche" || !commentaryOn || !user?.token) {
      prevHistoryLen.current = history.length;
      return;
    }
    if (history.length <= prevHistoryLen.current) return;
    prevHistoryLen.current = history.length;
    const last = history[history.length - 1];
    if (!last) return;
    const moveNum = history.length;
    const lastMoveText = last.black
      ? `${moveNum}... ${last.black} (your move)`
      : `${moveNum}. ${last.white} (BLANCHE)`;
    const pgn = history.map((p, i) => `${i + 1}. ${p.white}${p.black ? ' ' + p.black : ''}`).join(' ');
    narrateMove(user.token, pgn, lastMoveText)
      .then(data => { if (data?.text) setBlancheNarration(prev => [...prev, data.text]); })
      .catch(() => {});
  }, [history.length]); // eslint-disable-line

  // ── Show result modal after online game ends ──────────────────────────────
  useEffect(() => {
    if (over && multiplayer) setTimeout(() => setShowOnlineResult(true), 600);
  }, [over, multiplayer]); // eslint-disable-line

  // ── Update Elo when a ranked game result arrives ───────────────────────────
  useEffect(() => {
    if (eloChange?.newElo !== undefined) setElo(eloChange.newElo);
  }, [eloChange]);

  // ── Sound config — sync engine whenever prefs change ─────────────────────
  useEffect(() => {
    configureSound({
      sfxOn:        prefs.sfxOn,
      sfxVol:       prefs.sfxVol,
      ambientOn:    prefs.ambientOn,
      ambientVol:   prefs.ambientVol,
      ambientSong: prefs.ambientSong,
    });
  }, [prefs.sfxOn, prefs.sfxVol, prefs.ambientOn, prefs.ambientVol, prefs.ambientSong]); // eslint-disable-line

  // ── Move / capture sounds ─────────────────────────────────────────────────
  useEffect(() => {
    if (history.length === 0) return;
    if (history.length <= prevHistoryLen.current) return;
    prevHistoryLen.current = history.length;
    const isCapture = capW.length > prevCapWLen.current || capB.length > prevCapBLen.current;
    prevCapWLen.current = capW.length;
    prevCapBLen.current = capB.length;
    if (isCapture) playCapture();
    else           playMove();
  }, [history.length]); // eslint-disable-line

  // ── Check sound ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (status === prevStatus.current) return;
    prevStatus.current = status;
    if (status.includes('check') && !over) playCheck();
  }, [status]); // eslint-disable-line

  // ── Game over sound ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!over) return;
    const myColor = multiplayer?.playerColor || (mode === 'blanche' ? 'black' : 'white');
    const won = status.startsWith(myColor + ' wins');
    const isDraw = status.includes('draw');
    if (!isDraw) playGameOver(won);
  }, [over]); // eslint-disable-line

  // ── BLANCHE panel sound ───────────────────────────────────────────────────
  useEffect(() => {
    if (blancheOpen) playBlancheOpen();
  }, [blancheOpen]); // eslint-disable-line

  // ── Trial offer after first BLANCHE game ──────────────────────────────────
  useEffect(() => {
    if (!over || mode !== "blanche") return;
    const gamesPlayed = parseInt(localStorage.getItem('blanche_games_played') || '0');
    localStorage.setItem('blanche_games_played', String(gamesPlayed + 1));
    if (gamesPlayed > 0) return;
    if (localStorage.getItem('blanche_trial_offered')) return;
    localStorage.setItem('blanche_trial_offered', '1');
    if (!user?.token) return;
    getUserPurchases(user.token)
      .then(data => {
        if (data?.subscription?.status === 'active') {
          setSubTier(data.subscription.tier || 'arena');
        } else {
          setTimeout(() => setShowTrialModal(true), 2000);
        }
      })
      .catch(() => {});
  }, [over, mode]); // eslint-disable-line

  // ── Multiplayer: navigate to board, reset or replay on game start/reconnect ──
  // Keyed on gameId so it fires exactly once per game regardless of how many times
  // the multiplayer memo reference changes (e.g. when reconnectMoves is set).
  // reconnectMoves = null  → fresh game, reset to initial position
  // reconnectMoves = [...] → reconnect, replay move history
  useEffect(() => {
    if (!multiplayer) return;
    setTab("play");
    setScreen("app");
    setShowOnlineResult(false);
    setHintCount(0);
    setAutoHint(null);

    const moves = multiplayer.reconnectMoves;
    if (Array.isArray(moves)) {
      // Reconnect path: replay every move from the server's stored history
      let g = getInitialGame('pvp');
      for (const move of moves) {
        try {
          const { board: nb, capturedType } = applyMove(g.board, move.from, move.type, g.turn, move.to);
          g = finalizeMove(g, g.turn, nb, capturedType, move.from, move.to, move.type, move.promo || null);
        } catch { break; }
      }
      setGame({ ...g, flipped: multiplayer.playerColor === 'black', mode: 'pvp' });
    } else {
      // Fresh game path
      setGame(prev => ({
        ...getInitialGame('pvp'),
        difficulty: prev.difficulty,
        flipped: multiplayer.playerColor === 'black',
      }));
    }
  }, [multiplayer?.gameId]); // eslint-disable-line

  // ── Multiplayer: receive opponent moves ────────────────────────────────────
  useEffect(() => {
    if (!multiplayer) return;
    const { socket: sock } = multiplayer;
    const onOppMove = ({ move }) => {
      setGame(prev => {
        if (isGameOver(prev.status)) return prev;
        const { board: nb, capturedType } = applyMove(prev.board, move.from, move.type, prev.turn, move.to, move.promo || null);
        const next = finalizeMove(prev, prev.turn, nb, capturedType, move.from, move.to, move.type, move.promo || null);
        if (isGameOver(next.status)) setTimeout(() => handleGameOver(next.status), 0);
        return next;
      });
    };
    sock.on('opponent_move', onOppMove);
    return () => sock.off('opponent_move', onOppMove);
  }, [multiplayer]); // eslint-disable-line

  // ── Menu navigation ────────────────────────────────────────────────────────
  function goToPlay(mode) {
    newGame(mode);
    setTab("play");
    setScreen("app");
  }
  function goToTab(t) {
    setTab(t);
    setScreen("app");
  }

  // ── BLANCHE game start ─────────────────────────────────────────────────────
  function startBlancheGame() {
    const gamesPlayed = parseInt(localStorage.getItem('blanche_games_played') || '0');
    const isFirst = gamesPlayed === 0;
    setGame(prev => ({
      ...getInitialGame("blanche"),
      difficulty: prev.difficulty,
      flipped: true,
    }));
    const intro = isFirst
      ? [`Welcome to the board${user?.username ? `, ${user.username}` : ''}. I am BLANCHE. I have sat across from kings and watched them fall. You play Black. Do not play it safe — I would rather see bold mistakes than timid moves.`]
      : [];
    setBlancheNarration(intro);
    prevHistoryLen.current = 0;
    setTab("play");
    setScreen("app");
    setBlancheOpen(true);
  }

  // ── Controls ───────────────────────────────────────────────────────────────
  function newGame(nextMode, nextDiff) {
    setGame(prev => ({
      ...getInitialGame(nextMode || prev.mode),
      difficulty: nextDiff || prev.difficulty,
      flipped: prev.flipped,
    }));
    setHintCount(0);
    setAutoHint(null);
  }
  function resign() {
    if (over || botThinking) return;
    const winner = turn === "white" ? "black" : "white";
    const fs = `${winner} wins by resignation`;
    setGame(p => ({ ...p, status: fs, selected: null, selectedType: null, legalMoves: [], drawOffer: null }));
    setTimeout(() => handleGameOver(fs), 0);
  }
  function offerDraw() {
    if (over || mode === "bot" || botThinking || drawOffer) return;
    setGame(p => ({ ...p, drawOffer: p.turn, selected: null, selectedType: null, legalMoves: [] }));
  }
  function acceptDraw() {
    const fs = "draw by agreement";
    setGame(p => ({ ...p, status: fs, drawOffer: null, selected: null, selectedType: null, legalMoves: [] }));
    setTimeout(() => handleGameOver(fs), 0);
  }
  function declineDraw() { setGame(p => ({ ...p, drawOffer: null })); }

  async function handleStartTrial() {
    if (!user?.token || trialLoading) return;
    setTrialLoading(true);
    try {
      const data = await createTrialSession(user.token);
      if (data.url) { window.location.href = data.url; return; }
      // trial already used or not configured — just close the modal
      setShowTrialModal(false);
    } catch {
      setShowTrialModal(false);
    }
    setTrialLoading(false);
  }

  // ── Board helpers ──────────────────────────────────────────────────────────
  const SQ = BOARD_SQ_MAP[prefs.boardSize] || DS.sq;
  const squares = Array.from({ length: 64 }, (_, i) => flipped ? 63 - i : i);
  const files   = flipped ? ["h","g","f","e","d","c","b","a"] : ["a","b","c","d","e","f","g","h"];
  const ranks   = flipped ? [1,2,3,4,5,6,7,8] : [8,7,6,5,4,3,2,1];

  const promoOpts = promo?.color === "white"
    ? [["queen","♕"],["rook","♖"],["bishop","♗"],["knight","♘"]]
    : [["queen","♛"],["rook","♜"],["bishop","♝"],["knight","♞"]];

  // Status display helpers
  let statusLabel = status;
  let statusColor = DS.textMuted;
  if (over && status.includes("wins"))         { statusColor = DS.gold; }
  else if (over)                               { statusColor = DS.warning; }
  else if (status.includes("check"))           { statusColor = DS.danger; }
  else if (status.includes("to move"))         { statusColor = DS.text; }
  if (botThinking) statusLabel = `${DIFFICULTIES[difficulty]?.label || "Bot"} is thinking…`;

  const newTrophyCount = toasts.length;

  // ── Render ─────────────────────────────────────────────────────────────────

  if (screen === "menu") {
    return (
      <MainMenu
        onPlay={goToPlay}
        onNavigate={goToTab}
        stats={stats}
        onPlayOnline={onPlayOnline}
        onPlayBlanche={startBlancheGame}
        hasResumeGame={hasResumeGame}
        resumeExpired={resumeExpired}
        onResumeMatch={onResumeMatch}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: DS.bg, color: DS.text }}>

      {/* ── Opponent disconnect banner ─────────────────────────────────────── */}
      {opponentDisconnected && multiplayer && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 300,
          background: "rgba(224,140,40,0.96)", backdropFilter: "blur(4px)",
          padding: "10px 20px", textAlign: "center",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>⚠</span>
          <span className="cinzel" style={{ fontSize: 12, fontWeight: 700, color: "#fff", letterSpacing: "0.04em" }}>
            Opponent disconnected — waiting up to 45 seconds for them to return
          </span>
        </div>
      )}

      {/* ── Online game result modal ──────────────────────────────────────── */}
      {showOnlineResult && multiplayer && over && (() => {
        const myColor = multiplayer.playerColor || 'white';
        const isWin  = status.startsWith(myColor + ' wins');
        const isDraw = status.includes('draw');
        const resultLabel = isWin ? 'Victory' : isDraw ? 'Draw' : 'Defeat';
        const resultColor = isWin ? DS.gold : isDraw ? DS.warning : DS.danger;
        const resultIcon  = isWin ? '♔' : isDraw ? '½' : '♟';
        return (
          <div style={{
            position: "fixed", inset: 0, zIndex: 450,
            background: "rgba(13,12,24,0.85)", backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "fadeUp 0.25s ease both",
          }}>
            <div style={{
              background: DS.surface, border: `1px solid ${DS.borderFaint}`,
              borderRadius: 18, padding: "40px 36px", width: 320, textAlign: "center",
              boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
            }}>
              <div style={{
                fontSize: 52, color: resultColor, marginBottom: 12,
                filter: `drop-shadow(0 0 20px ${resultColor}66)`,
              }}>{resultIcon}</div>
              <div className="cinzel-deco" style={{
                fontSize: 26, fontWeight: 700, color: resultColor,
                letterSpacing: "0.08em", marginBottom: 6,
              }}>{resultLabel}</div>
              <div style={{ fontSize: 13, color: DS.textMuted, marginBottom: 20, lineHeight: 1.5 }}>
                {status}
              </div>
              {eloChange && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: eloChange.delta >= 0 ? `${DS.success}18` : `${DS.danger}18`,
                  border: `1px solid ${eloChange.delta >= 0 ? DS.success : DS.danger}40`,
                  borderRadius: 8, padding: "8px 16px", marginBottom: 24,
                }}>
                  <span style={{ fontSize: 14 }}>♟</span>
                  <span className="cinzel" style={{
                    fontSize: 14, fontWeight: 700,
                    color: eloChange.delta >= 0 ? DS.success : DS.danger,
                  }}>
                    {eloChange.delta >= 0 ? '+' : ''}{eloChange.delta} Elo &rarr; {eloChange.newElo}
                  </span>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  className="btn btn-primary cinzel"
                  style={{ width: "100%", fontSize: 13, letterSpacing: "0.06em" }}
                  onClick={() => { setShowOnlineResult(false); onPlayOnline(); }}
                >
                  ⚡ New Online Match
                </button>
                <button
                  className="cinzel"
                  style={{
                    width: "100%", padding: "10px 0", borderRadius: 8, cursor: "pointer",
                    background: "linear-gradient(135deg, rgba(237,187,74,0.14), rgba(115,102,240,0.10))",
                    border: `1px solid rgba(237,187,74,0.35)`,
                    color: DS.gold, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em",
                    fontFamily: "'Cinzel', serif",
                  }}
                  onClick={() => { setShowOnlineResult(false); setBlancheOpen(true); }}
                >
                  ♛ Review with Blanche
                </button>
                <button
                  className="btn btn-ghost btn-sm cinzel"
                  style={{ width: "100%" }}
                  onClick={() => { setShowOnlineResult(false); setScreen("menu"); }}
                >
                  ← Return to Menu
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <header style={{
        background: DS.surface,
        borderBottom: `1px solid ${DS.borderFaint}`,
        display: "flex", alignItems: "center",
        padding: "0 24px", height: 56, gap: 32,
        position: "sticky", top: 0, zIndex: 50,
        backdropFilter: "blur(12px)",
      }}>
        {/* Logo — click to return to main menu */}
        <div
          onClick={() => { setScreen("menu"); setBlancheOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, cursor: "pointer", userSelect: "none" }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}
        >
          <span style={{ fontSize: 20, color: DS.gold }}>♔</span>
          <span className="cinzel-deco" style={{ fontWeight: 700, fontSize: 15, letterSpacing: "0.05em", color: DS.text }}>
            Checkmate
          </span>
        </div>

        {/* Nav — hidden on mobile (bottom nav takes over) */}
        <nav style={{ display: isMobile ? "none" : "flex", alignItems: "flex-end", gap: 4, flex: 1, height: "100%", overflowX: "auto" }}>
          {[
            ["play",     "♟  Play"],
            ["puzzle",   "◎  Puzzle"],
            ["trophies", "♔  Trophies"],
            ["stats",    "◆  Stats"],
            ["rankings", "♕  Rankings"],
            ["armoury",  "◈  Armoury"],
            ["shop",     "✦  Shop"],
            ["settings", "⚙  Settings"],
          ].map(([t, label]) => (
            <button key={t} className={`nav-tab${tab === t ? " active" : ""}`}
              onClick={() => setTab(t)}
              style={{ marginTop: "auto" }}>
              {label}
              {t === "trophies" && newTrophyCount > 0 && (
                <span style={{
                  marginLeft: 5, background: DS.accent, color: "#fff",
                  borderRadius: 8, fontSize: 10, padding: "1px 5px", fontWeight: 700,
                }}>!</span>
              )}
            </button>
          ))}
        </nav>

        {/* Back to menu */}
        <button
          className="btn btn-ghost btn-sm cinzel"
          onClick={() => setScreen("menu")}
          style={{ flexShrink: 0, letterSpacing: "0.05em", fontSize: 11 }}
        >
          ← Menu
        </button>

        {/* Quick stats */}
        <div style={{ fontSize: 12, color: DS.textMuted, flexShrink: 0, fontWeight: 500 }}>
          <span style={{ color: DS.success }}>{stats.wins}W</span>
          <span style={{ margin: "0 4px", opacity: 0.3 }}>·</span>
          <span style={{ color: DS.danger }}>{stats.losses}L</span>
          <span style={{ margin: "0 4px", opacity: 0.3 }}>·</span>
          <span>{stats.draws}D</span>
        </div>

        {/* Crown balance */}
        {user && (
          <button
            className="cinzel"
            onClick={() => setTab("shop")}
            title="View Shop"
            style={{
              display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
              background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.22)",
              borderRadius: 6, padding: "4px 10px", cursor: "pointer",
            }}>
            <CrownCoin size={16} shimmer />
            <span style={{ fontSize: 11, fontWeight: 700, color: "#D4AF37", letterSpacing: "0.04em" }}>
              {crowns.toLocaleString()}
            </span>
          </button>
        )}

        {/* Elo badge */}
        {user && elo !== null && (
          <button onClick={() => setTab("rankings")} style={{
            background: "none", border: `1px solid ${DS.borderFaint}`,
            borderRadius: 6, padding: "4px 10px", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
          }}>
            <span style={{ fontSize: 10, color: DS.textFaint }}>♟</span>
            <span className="cinzel" style={{ fontSize: 11, fontWeight: 700, color: DS.textMuted, letterSpacing: "0.04em" }}>
              {elo}
            </span>
          </button>
        )}

        {/* Logged-in user indicator */}
        {user && (
          <button className="cinzel" onClick={() => setTab("settings")}
            style={{
              background: subTier === "pro"
                ? "linear-gradient(90deg, #C8950A, #FFD700, #FFF5C0, #FFD700, #C8950A)"
                : "none",
              WebkitBackgroundClip: subTier === "pro" ? "text" : undefined,
              WebkitTextFillColor: subTier === "pro" ? "transparent" : undefined,
              backgroundClip: subTier === "pro" ? "text" : undefined,
              border: `1px solid ${subTier === "pro" ? "rgba(237,187,74,0.4)" : DS.borderFaint}`,
              borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11,
              color: subTier === "pro" ? undefined : DS.textMuted,
              letterSpacing: "0.04em", flexShrink: 0,
            }}>
            ⚙ {user.username}
          </button>
        )}
      </header>

      {/* ══ TAB CONTENT ═════════════════════════════════════════════════════ */}
      <main style={{
        padding: tab === "play" ? "24px 24px" : "24px",
        maxWidth: tab === "play" ? "none" : 760,
        margin: tab === "play" ? undefined : "0 auto",
        marginLeft: (blancheOpen && !isMobile) ? 320 : undefined,
        paddingBottom: isMobile ? "calc(72px + env(safe-area-inset-bottom, 0px))" : undefined,
        transition: "margin-left 0.25s ease",
      }}>

        {/* ── PLAY ── */}
        {tab === "play" && (
          <div className="tab-content" style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>

            {/* LEFT: Board column */}
            <div style={{ flexShrink: 0 }}>

              {/* Black player bar */}
              <PlayerBar
                color="black"
                name={multiplayer
                  ? (multiplayer.playerColor === "black" ? (user?.username || "You") : (multiplayer.opponent?.username || "Opponent"))
                  : mode === "blanche" ? (user?.username || "You")
                  : (mode === "bot" ? `Bot · ${DIFFICULTIES[difficulty]?.label || ""}` : "Black")}
                captures={capB} isThinking={botThinking}
                isActive={!over && turn === "black"} checked={bCheck}
                SQ={SQ}
              />

              {/* Board */}
              <div style={{
                display: "inline-block",
                border: `3px solid #0A0913`,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 8px 24px rgba(0,0,0,0.5)",
                marginTop: 8, marginBottom: 8,
              }}>
                <div className={boardEffectClass || undefined} style={{ display: "grid", gridTemplateColumns: `repeat(8, ${SQ}px)`, position: "relative" }}>
                  {squares.map((sq, idx) => {
                    const row = Math.floor(sq / 8), col = sq % 8;
                    const isDark     = (row + col) % 2 === 1;
                    const piece      = getPieceAt(sq, board);
                    const isSel      = selected === sq;
                    const isLegal    = legalMoves.includes(sq);
                    const isCapt     = isLegal && piece !== "";
                    const isLast     = lastMove && (sq === lastMove.from || sq === lastMove.to);
                    const isWK       = board.white.king.includes(sq);
                    const isBK       = board.black.king.includes(sq);
                    const isChecked  = (wCheck && isWK) || (bCheck && isBK);
                    const pieceIsW   = isWP(sq, board);

                    let bg = isDark ? T.dark : T.light;
                    if      (isChecked) bg = DS.danger;
                    else if (isSel)     bg = T.selected;
                    else if (isLast)    bg = isDark ? T.lastDark : T.lastLight;

                    const pieceStyle = (blancheOpen && pieceIsW)
                      ? getPieceStyle("gilded", prefs.pieceColor, true)
                      : activePieceCosmetic
                        ? getPieceStyleFromCosmetic(activePieceCosmetic, pieceIsW)
                        : getPieceStyle(prefs.pieceDesign, prefs.pieceColor, pieceIsW);

                    // Coordinate overlays
                    const showRank = (!flipped ? col === 0 : col === 7);
                    const showFile = (!flipped ? row === 7 : row === 0);
                    const coordColor = isDark ? `rgba(${hexToRgb(T.light)},0.55)` : `rgba(${hexToRgb(T.dark)},0.55)`;

                    return (
                      <div key={sq}
                        className={`board-sq${over || botThinking ? " no-cursor" : ""}${sqEffectClass ? ` ${sqEffectClass}` : ""}`}
                        onClick={() => handleSquare(sq)}
                        style={{ width: SQ, height: SQ, background: bg }}>

                        {/* Coordinate overlays */}
                        {showRank && (
                          <span className="coord-rank" style={{ color: coordColor }}>
                            {ranks[Math.floor(idx / 8)]}
                          </span>
                        )}
                        {showFile && (
                          <span className="coord-file" style={{ color: coordColor }}>
                            {files[idx % 8]}
                          </span>
                        )}

                        {/* Piece */}
                        {piece && (
                          <span className="sq-piece" style={{ fontSize: SQ * 0.72, ...pieceStyle }}>{piece}</span>
                        )}

                        {/* Legal move dot */}
                        {isLegal && !isCapt && (
                          <span className="sq-dot"
                            style={{ background: `rgba(0,0,0,${isDark ? 0.28 : 0.20})` }} />
                        )}

                        {/* Capture ring */}
                        {isCapt && (
                          <span className="sq-ring"
                            style={{ border: `6px solid rgba(0,0,0,${isDark ? 0.28 : 0.20})` }} />
                        )}

                        {/* Selection ring */}
                        {isSel && <span className="sq-sel-ring" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* White player bar */}
              <PlayerBar
                color="white"
                name={multiplayer
                  ? (multiplayer.playerColor === "white" ? (user?.username || "You") : (multiplayer.opponent?.username || "Opponent"))
                  : mode === "blanche" ? "BLANCHE ♛" : "White"}
                captures={capW} isThinking={mode === "blanche" && botThinking}
                isActive={!over && turn === "white"} checked={wCheck}
                SQ={SQ}
              />
            </div>

            {/* RIGHT: Sidebar */}
            <div style={{ flex: "1 1 260px", minWidth: 240, maxWidth: 300, display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Controls card — hidden during online multiplayer */}
              {!multiplayer && <div className="card" style={{ padding: "16px" }}>
                <div style={sLabel}>Game Setup</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <select className="select-dark" value={mode}
                    onChange={e => newGame(e.target.value)}>
                    <option value="pvp">Player vs Player</option>
                    <option value="bot">vs Bot</option>
                  </select>
                  {mode === "bot" && (
                    <select className="select-dark" value={difficulty}
                      onChange={e => { localStorage.setItem('checkmate_last_bot_difficulty', e.target.value); setGame(p => ({ ...getInitialGame("bot"), difficulty: e.target.value, flipped: p.flipped })); }}>
                      {Object.entries(DIFFICULTIES).map(([k, cfg]) => (
                        <option key={k} value={k}>{cfg.label}</option>
                      ))}
                    </select>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }}
                      onClick={() => setGame(p => ({ ...p, flipped: !p.flipped }))}>
                      ⇄ Flip
                    </button>
                    <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
                      onClick={() => newGame()}>
                      New Game
                    </button>
                  </div>
                </div>
              </div>}

              {/* Status card */}
              <div className="card" style={{ padding: "16px" }}>
                <div style={sLabel}>Status</div>

                {/* Active win streak badge */}
                {stats.currentStreak >= 2 && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 7,
                    background: `linear-gradient(135deg, rgba(237,187,74,0.15), rgba(237,187,74,0.06))`,
                    border: `1px solid rgba(237,187,74,0.3)`,
                    borderRadius: 6, padding: "5px 10px", marginBottom: 10,
                  }}>
                    <span style={{ fontSize: 13, color: DS.gold }}>
                      {stats.currentStreak >= 25 ? "⚜" : stats.currentStreak >= 10 ? "✶" : stats.currentStreak >= 5 ? "‡" : "†"}
                    </span>
                    <span className="cinzel" style={{ fontSize: 11, fontWeight: 700, color: DS.gold, letterSpacing: "0.06em" }}>
                      {stats.currentStreak} Win Streak
                    </span>
                  </div>
                )}

                <div style={{ fontSize: 14, fontWeight: 600, color: statusColor, marginBottom: 12, lineHeight: 1.4 }}>
                  {botThinking && <span style={{ marginRight: 6 }}>⏳</span>}
                  {statusLabel}
                </div>

                {/* Elo change after online game */}
                {over && multiplayer && eloChange && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: eloChange.delta >= 0 ? `${DS.success}14` : `${DS.danger}14`,
                    border: `1px solid ${eloChange.delta >= 0 ? DS.success : DS.danger}30`,
                    borderRadius: 6, padding: "5px 10px", marginBottom: 10,
                  }}>
                    <span style={{ fontSize: 11 }}>♟</span>
                    <span className="cinzel" style={{
                      fontSize: 12, fontWeight: 700,
                      color: eloChange.delta >= 0 ? DS.success : DS.danger,
                    }}>
                      {eloChange.delta >= 0 ? "+" : ""}{eloChange.delta} Elo → {eloChange.newElo}
                    </span>
                  </div>
                )}

                {/* Post-game online actions */}
                {over && multiplayer && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                    <button
                      className="btn btn-primary cinzel"
                      style={{ width: "100%", fontSize: 12, letterSpacing: "0.06em" }}
                      onClick={onPlayOnline}
                    >
                      ⚡ New Online Match
                    </button>
                    <button
                      className="btn btn-ghost btn-sm cinzel"
                      style={{ width: "100%" }}
                      onClick={() => setScreen("menu")}
                    >
                      ← Return to Menu
                    </button>
                  </div>
                )}

                {/* Draw offer */}
                {drawOffer && !over && (
                  <div style={{
                    background: "rgba(232,160,48,0.12)", border: "1px solid rgba(232,160,48,0.3)",
                    borderRadius: 8, padding: "10px 12px", marginBottom: 10, fontSize: 13,
                  }}>
                    <div style={{ color: DS.warning, fontWeight: 600, marginBottom: 8 }}>
                      {drawOffer} offered a draw
                    </div>
                    {drawOffer !== turn && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" style={{ color: DS.success, borderColor: "rgba(75,184,122,0.4)" }}
                          onClick={acceptDraw}>Accept</button>
                        <button className="btn btn-danger btn-sm" onClick={declineDraw}>Decline</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Halfmove bar */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: DS.textMuted, marginBottom: 5 }}>
                    <span>50-move rule</span>
                    <span style={{ fontWeight: 600 }}>{halfmove}/100</span>
                  </div>
                  <div style={{ height: 4, background: DS.elevated, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 2, transition: "width 0.3s, background 0.3s",
                      width: `${Math.min(100, halfmove)}%`,
                      background: halfmove > 75 ? DS.danger : halfmove > 50 ? DS.warning : DS.success,
                    }} />
                  </div>
                </div>

                {/* Action buttons */}
                {!over && !botThinking && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-danger btn-sm" style={{ flex: 1 }} onClick={resign}>
                      Resign
                    </button>
                    {mode !== "bot" && !drawOffer && (
                      <button className="btn btn-gold btn-sm" style={{ flex: 1 }} onClick={offerDraw}>
                        ½ Draw
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Move history card */}
              <div className="card" style={{ padding: "16px", flex: 1 }}>
                <div style={sLabel}>Move History</div>
                <div style={{ maxHeight: 280, overflowY: "auto" }}>
                  {history.length === 0 ? (
                    <div style={{ fontSize: 13, color: DS.textFaint, textAlign: "center", padding: "20px 0" }}>
                      No moves yet
                    </div>
                  ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <tbody>
                        {history.map((m, i) => (
                          <tr key={i} className="tbl-row" style={{ borderBottom: `1px solid ${DS.borderFaint}` }}>
                            <td style={{ padding: "4px 6px", color: DS.textFaint, width: 28, fontSize: 11 }}>{i + 1}</td>
                            <td style={{ padding: "4px 8px", fontFamily: "monospace", color: DS.textMuted, fontWeight: 500 }}>{m.white}</td>
                            <td style={{ padding: "4px 8px", fontFamily: "monospace", color: DS.textMuted, fontWeight: 500 }}>{m.black}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* BLANCHE buttons — desktop only */}
              {!isMobile && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                  <button
                    onClick={() => setBlancheOpen(o => !o)}
                    className="cinzel"
                    style={{
                      width: "100%", padding: "11px 0",
                      background: blancheOpen
                        ? `linear-gradient(135deg, rgba(237,187,74,0.18), rgba(115,102,240,0.14))`
                        : `linear-gradient(135deg, rgba(237,187,74,0.08), rgba(115,102,240,0.06))`,
                      border: `1px solid ${blancheOpen ? "rgba(237,187,74,0.45)" : "rgba(237,187,74,0.2)"}`,
                      borderRadius: DS.radiusSm, cursor: "pointer", color: DS.gold,
                      fontSize: 11, letterSpacing: "0.1em", fontWeight: 700, transition: "all 0.15s",
                    }}
                  >
                    ♛  {over ? "Review with Blanche" : "Blanche"}
                  </button>
                  {!over && mode !== "blanche" && (
                    <button
                      onClick={() => {
                        const hint = `I'm playing as ${mode === 'pvp' ? 'White' : (multiplayer?.playerColor || 'White')}. What's the best plan in this position?`;
                        setAutoHint(hint);
                        setHintCount(c => c + 1);
                        setBlancheOpen(true);
                      }}
                      className="cinzel"
                      style={{
                        width: "100%", padding: "8px 0",
                        background: "rgba(115,102,240,0.07)",
                        border: `1px solid rgba(115,102,240,0.2)`,
                        borderRadius: DS.radiusSm, cursor: "pointer", color: DS.accent,
                        fontSize: 10, letterSpacing: "0.08em", fontWeight: 700, transition: "all 0.15s",
                      }}
                    >
                      ◎  Ask Blanche
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── OTHER TABS ── */}
        <div className="tab-content">
          {tab === "puzzle"    && <DailyPuzzle  user={user} crowns={crowns} onCrownsUpdate={setCrowns} onNavigate={(key) => { if (key === "menu") { setScreen("menu"); } else { goToTab(key); } }} onPlayBlanche={startBlancheGame} />}
          {tab === "trophies"  && <TrophyCase  stats={stats} />}
          {tab === "stats"     && <StatsPanel  stats={stats} />}
          {tab === "rankings"  && <Leaderboard stats={stats} user={user} elo={elo} />}
          {tab === "armoury"   && <Armoury     prefs={prefs} onPrefsChange={updatePrefs} stats={stats} user={user} />}
          {tab === "shop"      && <Shop        prefs={prefs} user={user} crowns={crowns} onCrownsUpdate={setCrowns} />}
          {tab === "settings"  && <AccountSettings user={user} prefs={prefs} onPrefsChange={updatePrefs} onLogout={onLogout} onCrownsUpdate={setCrowns} />}

        </div>
      </main>

      {/* ══ MOBILE BOTTOM NAV ═══════════════════════════════════════════════ */}
      {isMobile && (
        <nav style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 190,
          background: DS.surface, borderTop: `1px solid ${DS.borderFaint}`,
          display: "flex", overflowX: "auto", WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none", paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}>
          <style>{`nav::-webkit-scrollbar { display: none; }`}</style>
          {[
            ["play",     "♟", "Play"],
            ["puzzle",   "◎", "Puzzle"],
            ["trophies", "♔", "Trophies"],
            ["stats",    "◆", "Stats"],
            ["rankings", "♕", "Rankings"],
            ["armoury",  "◈", "Armoury"],
            ["shop",     "✦", "Shop"],
            ["settings", "⚙", "Settings"],
          ].map(([t, icon, lbl]) => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: "0 0 auto", minWidth: 68, padding: "10px 8px 8px",
              border: "none", background: "transparent", cursor: "pointer",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
              borderTop: `2px solid ${tab === t ? DS.accent : "transparent"}`,
              transition: "border-color 0.15s",
            }}>
              <span style={{ fontSize: 16, color: tab === t ? DS.accent : DS.textMuted, position: "relative" }}>
                {icon}
                {t === "trophies" && newTrophyCount > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -6,
                    background: DS.accent, color: "#fff",
                    borderRadius: "50%", fontSize: 8, width: 14, height: 14,
                    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
                  }}>!</span>
                )}
              </span>
              <span className="cinzel" style={{
                fontSize: 8, fontWeight: 700, letterSpacing: "0.06em",
                color: tab === t ? DS.accent : DS.textFaint, textTransform: "uppercase",
              }}>{lbl}</span>
            </button>
          ))}
          {/* BLANCHE pill in the middle of bottom nav */}
          <button onClick={() => setBlancheOpen(o => !o)} style={{
            flex: "0 0 auto", minWidth: 80, padding: "8px 12px",
            border: "none", cursor: "pointer", order: -1,
            background: "linear-gradient(135deg, #FFD700, #FFF5C0 40%, #FFD700 70%, #C8950A)",
            borderRadius: 0, borderTop: "2px solid transparent",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
          }}>
            <span style={{ fontSize: 16 }}>♛</span>
            <span className="cinzel" style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.06em", color: "#1A1200" }}>BLANCHE</span>
          </button>
        </nav>
      )}

      {/* ══ BLANCHE PANEL ═══════════════════════════════════════════════════ */}
      {blancheOpen && (
        <Blanche
          user={user}
          currentPgn={history.map((pair, i) => `${i + 1}. ${pair.white}${pair.black ? ' ' + pair.black : ''}`).join(' ')}
          playerColor={multiplayer?.playerColor || (mode === 'blanche' ? 'black' : 'white')}
          onClose={() => { setBlancheOpen(false); setAutoHint(null); }}
          isMobile={isMobile}
          liveMessages={blancheNarration}
          isBlancheGame={mode === "blanche" && !over}
          gameOver={over}
          onStartBlancheGame={startBlancheGame}
          commentaryOn={commentaryOn}
          onToggleCommentary={() => setCommentaryOn(c => !c)}
          onNavigate={goToTab}
          onPlayOnline={onPlayOnline}
          onNewGame={() => newGame()}
          autoHint={autoHint}
          hintCount={hintCount}
          onHintUsed={() => setHintCount(c => c + 1)}
          subTier={subTier}
        />
      )}

      {/* ══ PROMOTION MODAL ═════════════════════════════════════════════════ */}
      {promo && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
          backdropFilter: "blur(4px)",
        }}>
          <div style={{
            background: DS.elevated, borderRadius: 16, padding: 28,
            border: `1px solid ${DS.borderMid}`,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
            boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: DS.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Promote pawn
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {promoOpts.map(([piece, icon]) => (
                <button key={piece} onClick={() => handlePromo(piece)} style={{
                  width: 72, height: 72, fontSize: 44, background: DS.surface,
                  border: `1px solid ${DS.border}`, borderRadius: 10,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.12s", color: DS.text,
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = DS.elevated; e.currentTarget.style.borderColor = DS.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.background = DS.surface; e.currentTarget.style.borderColor = DS.border; }}>
                  {icon}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ BLANCHE TRIAL MODAL ═════════════════════════════════════════════ */}
      {showTrialModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(8,7,18,0.92)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 600, backdropFilter: "blur(8px)",
          animation: "fadeUp 0.4s ease",
        }}>
          <div style={{
            background: `linear-gradient(160deg, #12102A 0%, #0D0C1F 60%, #16111E 100%)`,
            border: `1px solid rgba(237,187,74,0.25)`,
            borderRadius: 20, padding: "44px 40px", textAlign: "center",
            boxShadow: `0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(237,187,74,0.08), inset 0 1px 0 rgba(237,187,74,0.1)`,
            maxWidth: 400, width: "calc(100vw - 48px)",
          }}>
            {/* Crown */}
            <div style={{
              fontSize: 52, color: DS.gold, marginBottom: 20,
              filter: `drop-shadow(0 0 24px ${DS.goldGlow}) drop-shadow(0 0 8px rgba(237,187,74,0.6))`,
              lineHeight: 1,
            }}>♛</div>

            {/* Headline */}
            <div className="cinzel" style={{
              fontSize: 10, color: DS.textFaint, letterSpacing: "0.25em",
              textTransform: "uppercase", marginBottom: 14,
            }}>
              Your First Lesson Is Complete
            </div>
            <div className="cinzel-deco" style={{
              fontSize: 19, color: DS.text, marginBottom: 20, lineHeight: 1.35,
            }}>
              Continue Your Training
            </div>

            {/* BLANCHE's words */}
            <div style={{
              fontSize: 13, color: DS.textMuted, lineHeight: 1.8,
              marginBottom: 28, fontStyle: "italic",
              borderLeft: `2px solid rgba(237,187,74,0.3)`,
              paddingLeft: 14, textAlign: "left",
            }}>
              "{user?.username ? `${user.username}, ` : ''}I have ruled kingdoms with less to work with than what I saw in that game. You have instincts. Let me sharpen them."
            </div>

            {/* Offer card */}
            <div style={{
              background: "rgba(237,187,74,0.06)",
              border: "1px solid rgba(237,187,74,0.18)",
              borderRadius: 12, padding: "18px 20px", marginBottom: 24,
            }}>
              <div className="cinzel" style={{ fontSize: 16, color: DS.gold, fontWeight: 700, marginBottom: 6 }}>
                14 Days Free
              </div>
              <div style={{ fontSize: 12, color: DS.textMuted, lineHeight: 1.6, marginBottom: 4 }}>
                Unlimited sessions with BLANCHE — live commentary, full game analysis, a coach who remembers everything.
              </div>
              <div style={{ fontSize: 11, color: DS.textFaint, marginTop: 8 }}>
                Then $4.99 / month — cancel anytime
              </div>
            </div>

            {/* Buttons */}
            <button
              className="cinzel"
              onClick={handleStartTrial}
              disabled={trialLoading}
              style={{
                width: "100%", padding: "14px 0", marginBottom: 12,
                background: trialLoading
                  ? "rgba(237,187,74,0.3)"
                  : "linear-gradient(135deg, #FFD700 0%, #F0C030 40%, #C8950A 100%)",
                border: "none", borderRadius: 10, cursor: trialLoading ? "default" : "pointer",
                color: "#1A1200", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em",
                boxShadow: trialLoading ? "none" : "0 4px 20px rgba(237,187,74,0.35)",
                transition: "all 0.2s",
              }}
            >
              {trialLoading ? "Opening Stripe…" : "▶ START FREE TRIAL"}
            </button>

            <button
              className="cinzel"
              onClick={() => setShowTrialModal(false)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: DS.textFaint, fontSize: 10, letterSpacing: "0.12em",
                textDecoration: "none", padding: "6px",
              }}
            >
              MAYBE LATER
            </button>
          </div>
        </div>
      )}

      {/* ══ ONBOARDING MODAL ════════════════════════════════════════════════ */}
      {showOnboarding && (
        <OnboardingModal
          username={user?.username}
          onClose={() => {
            localStorage.setItem('checkmate_onboarded', '1');
            setShowOnboarding(false);
          }}
          onPlayOnline={() => {
            localStorage.setItem('checkmate_onboarded', '1');
            setShowOnboarding(false);
            if (onPlayOnline) onPlayOnline();
          }}
          onPlayBlanche={() => {
            localStorage.setItem('checkmate_onboarded', '1');
            setShowOnboarding(false);
            startBlancheGame();
          }}
        />
      )}

      {/* ══ TROPHY TOAST ════════════════════════════════════════════════════ */}
      {toasts.length > 0 && (
        <div onClick={() => setToasts(p => p.slice(1))} style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 300,
          background: `linear-gradient(135deg, ${DS.elevated}, #2A2648)`,
          border: `1px solid rgba(115,102,240,0.35)`,
          borderRadius: 14, padding: "14px 18px",
          boxShadow: `0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(115,102,240,0.15)`,
          cursor: "pointer", maxWidth: 300,
          display: "flex", alignItems: "center", gap: 14,
          animation: "toastIn 0.3s ease",
        }}>
          <div style={{ fontSize: 36, flexShrink: 0 }}>{toasts[0].icon}</div>
          <div>
            <div style={{ fontSize: 10, color: DS.accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>
              Trophy Unlocked
            </div>
            <div style={{ fontWeight: 700, fontSize: 15, color: DS.text }}>{toasts[0].name}</div>
            <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 2 }}>{toasts[0].desc}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PLAYER BAR
// ─────────────────────────────────────────────────────────────────────────────

function PlayerBar({ color, name, captures, isActive, isThinking, checked, SQ }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 4px", width: SQ * 8,
      opacity: isActive ? 1 : 0.55,
      transition: "opacity 0.2s",
    }}>
      {/* Color dot */}
      <div style={{
        width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
        background: color === "white" ? "#E8E6F0" : "#1a1729",
        border: color === "white" ? "1px solid rgba(255,255,255,0.3)" : "1px solid rgba(255,255,255,0.15)",
        boxShadow: isActive ? `0 0 0 2px ${DS.accent}` : "none",
        transition: "box-shadow 0.2s",
      }} />

      {/* Name */}
      <span style={{ fontSize: 13, fontWeight: 600, color: DS.text }}>{name}</span>

      {/* Check warning */}
      {checked && (
        <span style={{ fontSize: 11, color: DS.danger, fontWeight: 700, background: "rgba(224,70,70,0.12)", padding: "2px 7px", borderRadius: 4 }}>
          CHECK
        </span>
      )}

      {/* Thinking pulse */}
      {isThinking && (
        <span style={{ fontSize: 11, color: DS.textMuted, fontStyle: "italic" }}>thinking…</span>
      )}

      {/* Captured pieces */}
      {captures && captures.length > 0 && (
        <span style={{ fontSize: 15, letterSpacing: 1, marginLeft: "auto", opacity: 0.75 }}>
          {captures.join("")}
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Convert hex to "r,g,b" for use in rgba()
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

const sLabel = {
  fontFamily: "'Cinzel', Georgia, serif",
  fontSize: 10, fontWeight: 700, color: DS.textFaint,
  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10,
};

// ─────────────────────────────────────────────────────────────────────────────
//  ONBOARDING MODAL
// ─────────────────────────────────────────────────────────────────────────────

const ONBOARDING_STEPS = [
  {
    icon: "♔",
    title: "Welcome to Checkmate",
    body: "Chess with stakes. Real opponents, real ratings, an AI coach who pulls no punches, and cosmetics earned on the board — not bought with a credit card.",
    cta: "Next →",
  },
  {
    icon: "⚔",
    title: "Find Your First Match",
    body: "Hit Play Online to be matched with a real opponent in seconds. Your Elo rating updates after every game — win, draw, or lose.",
    cta: "Next →",
  },
  {
    icon: "♛",
    title: "Train with BLANCHE",
    body: "BLANCHE is your personal chess coach. She plays White, narrates every move, and breaks down exactly what you should have done differently. One free session — on us.",
    cta: "Let's go",
  },
];

function OnboardingModal({ username, onClose, onPlayOnline, onPlayBlanche }) {
  const [step, setStep] = useState(0);
  const s = ONBOARDING_STEPS[step];
  const isLast = step === ONBOARDING_STEPS.length - 1;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(8,7,18,0.92)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 700, backdropFilter: "blur(8px)", animation: "fadeUp 0.3s ease",
    }}>
      <div style={{
        background: DS.surface, border: `1px solid ${DS.borderFaint}`,
        borderRadius: 20, padding: "44px 40px", maxWidth: 420,
        width: "calc(100vw - 48px)", textAlign: "center",
        boxShadow: "0 40px 100px rgba(0,0,0,0.7)",
      }}>
        {/* Step dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 32 }}>
          {ONBOARDING_STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 20 : 6, height: 6, borderRadius: 3,
              background: i === step ? DS.accent : DS.borderFaint,
              transition: "all 0.25s",
            }} />
          ))}
        </div>

        <div style={{ fontSize: 48, marginBottom: 20, color: DS.gold,
          filter: `drop-shadow(0 0 16px ${DS.goldGlow})` }}>{s.icon}</div>

        <div className="cinzel" style={{ fontSize: 16, fontWeight: 700, color: DS.text, marginBottom: 14 }}>
          {s.title}
        </div>
        <div style={{ fontSize: 13, color: DS.textMuted, lineHeight: 1.75, marginBottom: 32 }}>
          {step === 0 && username ? `Welcome, ${username}. ` : ""}{s.body}
        </div>

        {isLast ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button className="cinzel" onClick={onPlayBlanche} style={{
              padding: "13px 0", background: `linear-gradient(135deg, #FFD700, #C8950A)`,
              border: "none", borderRadius: 10, cursor: "pointer",
              color: "#1A1200", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
            }}>
              ♛ Play vs BLANCHE
            </button>
            <button className="cinzel" onClick={onPlayOnline} style={{
              padding: "13px 0", background: DS.accent,
              border: "none", borderRadius: 10, cursor: "pointer",
              color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
            }}>
              ⚔ Find Online Match
            </button>
            <button onClick={onClose} style={{
              background: "none", border: "none", cursor: "pointer",
              color: DS.textFaint, fontSize: 11, marginTop: 4,
            }}>
              Skip — explore on my own
            </button>
          </div>
        ) : (
          <button className="cinzel" onClick={() => setStep(s => s + 1)} style={{
            padding: "13px 0", width: "100%", background: DS.accent,
            border: "none", borderRadius: 10, cursor: "pointer",
            color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
          }}>
            {s.cta}
          </button>
        )}
      </div>
    </div>
  );
}
